import React, { createContext, useContext, useEffect, useRef } from 'react'

import type { MenuProps } from 'antd'
import { useSelectoRef } from 'features/common/ui/MultiSelectTool'
import { useGlobalDropzone } from 'features/dropzone/useGlobalDropzone'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { ROUTES } from 'shared/constants/routesPaths'
import classNames from 'classnames'

import { Header } from './Header'
import { Sidebar } from './Sidebar'

// Create a context for the scroll container reference
export const ScrollContainerContext = createContext<React.RefObject<HTMLDivElement> | null>(null)

// Hook to use the scroll container reference
export const useScrollContainer = () => useContext(ScrollContainerContext)

interface LayoutProps {
  children: React.ReactNode;
  breadcrumbs?: React.ReactNode;
  menuItems: MenuProps['items'];
  onShare?: () => void;
  onUpload?: (files: File[]) => void;
  selectedKeys: string[];
  boardsItems: any[];
  onAddBoard?: () => void;
  onOpenBoard: (boardId: any) => void;
  teamName: string
  isPublicLink?: boolean
  onAssetDropOnBoard?: (assetIds: string[], boardId: string) => void;
  onBoardDropOnBoard?: (boardIds: string[], targetBoardId: string) => void;
}

// Update the Sidebar component prop type to fix the type error
type SidebarProps = {
  items: MenuProps['items'];
  selectedKeys: string[];
  boardsItems: any[];
  onAddBoard: (() => void) | undefined;
  onOpenBoard: (boardId: any) => void;
  teamName: string;
  onAssetDropOnBoard?: (assetIds: string[], boardId: string) => void;
  onBoardDropOnBoard?: (boardIds: string[], targetBoardId: string) => void;
}

// Augment the Sidebar component with the correct types
const TypedSidebar: React.FC<SidebarProps> = Sidebar as any

export const DashboardLayout: React.FC<LayoutProps> = ({
  children,
  breadcrumbs,
  menuItems,
  onShare,
  onUpload,
  selectedKeys,
  boardsItems,
  onAddBoard,
  onOpenBoard,
  teamName,
  isPublicLink = false,
  onAssetDropOnBoard,
  onBoardDropOnBoard
}) => {
  const location = useLocation()
  const pathname = location.pathname
  const hideHeader = pathname.startsWith("/profile")
  const { getRootProps } = useGlobalDropzone()
  const scrollContainerRef = useRef<any>(null)
  const selectoRef = useSelectoRef()
  const layoutRef = useRef<HTMLDivElement>(null)

  const isAiAssistant = pathname.startsWith(ROUTES.dashboard.aiAssistant)

  useEffect(() => {
    if (layoutRef.current) {
      const savedWidth = localStorage.getItem('sidebarWidth')
      if (savedWidth) {
        layoutRef.current.style.setProperty('--sidebar-width', `${savedWidth}px`)
      }
    }
  }, [])

  return (
    <SLayoutContainer
      ref={layoutRef}
      {...getRootProps()}
    >
      {!isPublicLink && (
        <TypedSidebar
          items={menuItems}
          selectedKeys={selectedKeys}
          boardsItems={boardsItems}
          onAddBoard={onAddBoard}
          onOpenBoard={onOpenBoard}
          teamName={teamName}
          onAssetDropOnBoard={onAssetDropOnBoard}
          onBoardDropOnBoard={onBoardDropOnBoard}
        />
      )}

      <SMainContent>
        {!hideHeader && !isPublicLink && (
          <Header
            breadcrumbs={breadcrumbs}
            onShare={onShare}
            onUpload={onUpload}
          />
        )}

        <SContentArea
          ref={scrollContainerRef}
          className={classNames('js-layout-content-area', { 'no-horizontal-padding': isAiAssistant })}
          onScroll={() => {
            if (selectoRef?.current) {
              selectoRef.current.checkScroll()
            }
          }}
        >
          <ScrollContainerContext.Provider value={scrollContainerRef}>
            {children}
          </ScrollContainerContext.Provider>
        </SContentArea>
      </SMainContent>
    </SLayoutContainer>
  )
}

const SLayoutContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100vh;
  overflow: hidden;
`

const SMainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const SContentArea = styled.div`
  flex: 1;
  padding: 36px 24px;
  overflow-y: auto;
  background-color: white;

  &.no-horizontal-padding {
    padding: 36px 0;
  }
`
